import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { DgiFetchAPI } from "../../../api/Dgi";
import "./customizeTableInvoiceModal.css";

/* "Personalizar factura" de la cuenta de la mesa. Mismo flujo que el del Centro de Pagos del POS:
   Nombre + RUC/cédula -> "Validar" con la DGI -> resumen "Datos del usuario". Si la DGI no lo encuentra
   (2 intentos) o no responde, se cargan nombre y DV a mano.
   saved: { client_name, ruc, dv } | null. onSave({ client_name, ruc, dv }) y onRemove() devuelven promesa. */
export default function CustomizeTableInvoiceModal({ open, saved, onClose, onSave, onRemove }) {
  const [name, setName] = useState("");
  const [ruc, setRuc] = useState("");
  const [dv, setDv] = useState("");
  const [validated, setValidated] = useState(false); // datos listos (DGI o manual) -> muestra el resumen
  const [manual, setManual] = useState(false); // la DGI no lo encontro: nombre y DV a mano
  const [attempts, setAttempts] = useState(0);
  const [busy, setBusy] = useState(""); // "" | "validating" | "saving"
  const [message, setMessage] = useState(null); // { kind: ok | warn | error, text }

  useEffect(() => {
    if (!open) return;
    setName(saved?.client_name || "");
    setRuc(saved?.ruc || "");
    setDv(saved?.dv || "");
    setValidated(Boolean(saved?.ruc));
    setManual(false);
    setAttempts(0);
    setBusy("");
    setMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const close = () => { if (!busy) onClose?.(); };

  const resetRuc = () => {
    setRuc("");
    setDv("");
    setValidated(false);
    setManual(false);
    setAttempts(0);
    setMessage(null);
  };

  const validate = async () => {
    const rucValue = ruc.trim();
    if (!rucValue) { setMessage({ kind: "error", text: "Ingresa un RUC o cédula para validar." }); return; }
    if (busy) return;
    setBusy("validating");
    setMessage(null);
    try {
      const response = await DgiFetchAPI.checkDGIContribuyent(rucValue);
      const dgi = response?.data?.dgi_response;
      if (dgi?.gResProc?.dCodRes === "0680") {
        const dgiDv = String(dgi.dDV || "");
        setName(dgi.dNomb || name);
        setRuc(dgi.dRuc || rucValue);
        setDv(dgiDv && dgiDv.length < 2 ? `0${dgiDv}` : dgiDv);
        setValidated(true);
        setManual(false);
        setAttempts(0);
        setMessage({ kind: "ok", text: "Datos obtenidos de la DGI." });
      } else if (attempts < 1) {
        setAttempts(attempts + 1);
        setMessage({ kind: "warn", text: "Datos no encontrados en la DGI. Verifica el número e intenta nuevamente." });
      } else {
        setManual(true);
        setAttempts(0);
        setMessage({ kind: "warn", text: "No encontramos el RUC en la DGI. Ingresa los datos manualmente." });
      }
    } catch (err) {
      console.error(err);
      setManual(true);
      setAttempts(0);
      setMessage({ kind: "warn", text: "No pudimos consultar la DGI. Ingresa los datos manualmente." });
    } finally {
      setBusy("");
    }
  };

  // "Cargar datos" del modo manual: nombre y DV escritos por el cliente.
  const loadManual = () => {
    if (!name.trim()) { setMessage({ kind: "error", text: "Escribe el nombre para la factura." }); return; }
    if (!dv.trim() || !/^\d{1,3}$/.test(dv.trim())) { setMessage({ kind: "error", text: "El DV es requerido y solo lleva números." }); return; }
    setValidated(true);
    setManual(false);
    setMessage(null);
  };

  const save = async () => {
    if (busy) return;
    const trimmedName = name.trim();
    if (!trimmedName) { setMessage({ kind: "error", text: "Escribe el nombre con el que quieres la factura." }); return; }
    if (ruc.trim() && !validated) {
      setMessage({ kind: "error", text: "Valida el RUC con el botón «Validar» (o ingresa los datos manualmente) antes de guardar." });
      return;
    }
    setBusy("saving");
    setMessage(null);
    try {
      await onSave?.({ client_name: trimmedName, ruc: validated ? ruc.trim() : "", dv: validated ? dv.trim() : "" });
    } catch (err) {
      setMessage({ kind: "error", text: err?.response?.data?.error || "No fue posible guardar los datos de la factura. Intenta de nuevo." });
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy("saving");
    setMessage(null);
    try {
      await onRemove?.();
    } catch (err) {
      setMessage({ kind: "error", text: err?.response?.data?.error || "No fue posible quitar la personalización. Intenta de nuevo." });
    } finally {
      setBusy("");
    }
  };

  return createPortal(
    <div className="bill-mdl-overlay" onClick={close}>
      <div className="bill-mdl-dialog" role="dialog" aria-modal="true" aria-label="Personalizar factura" onClick={(e) => e.stopPropagation()}>
        <header className="bill-mdl-head">
          <i className="uil uil-edit" />
          <h2 className="bill-mdl-title">Personalizar factura</h2>
          <button type="button" className="bill-mdl-close" aria-label="Cerrar" onClick={close}>
            <i className="uil uil-times" />
          </button>
        </header>

        <div className="bill-mdl-body">
          {!validated ? (
            <section className="bill-mdl-card">
              <h3 className="bill-mdl-card-title">Carga de datos</h3>

              {manual ? (
                <>
                  <div className="bill-mdl-readonly">
                    <span className="bill-mdl-readonly-key">RUC</span>
                    <span className="bill-mdl-readonly-val">{ruc}</span>
                  </div>
                  <div className="bill-mdl-field">
                    <label className="bill-mdl-label" htmlFor="tim-name-m">Nombre</label>
                    <input id="tim-name-m" className="bill-mdl-input" placeholder="Nombre" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div className="bill-mdl-field">
                    <label className="bill-mdl-label" htmlFor="tim-dv-m">DV</label>
                    <input id="tim-dv-m" className="bill-mdl-input bill-mdl-input--short" inputMode="numeric" placeholder="00" maxLength={3} value={dv} onChange={(e) => setDv(e.target.value)} />
                  </div>
                  <div className="bill-mdl-actions">
                    <button type="button" className="bill-mdl-btn bill-mdl-btn--ghost" onClick={resetRuc}>Ingresar nuevo RUC</button>
                    <button type="button" className="bill-mdl-btn bill-mdl-btn--primary" onClick={loadManual}>Cargar datos</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="bill-mdl-field">
                    <label className="bill-mdl-label" htmlFor="tim-name">Nombre</label>
                    <input id="tim-name" className="bill-mdl-input" placeholder="Nombre" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div className="bill-mdl-field">
                    <label className="bill-mdl-label" htmlFor="tim-ruc">RUC o cédula (opcional)</label>
                    <div className="bill-mdl-inline">
                      <input
                        id="tim-ruc"
                        type="text"
                        className="bill-mdl-input"
                        placeholder="0-000-000"
                        maxLength={30}
                        value={ruc}
                        onChange={(e) => { setRuc(e.target.value); setAttempts(0); }}
                      />
                      <button type="button" className="bill-mdl-btn bill-mdl-btn--primary" onClick={validate} disabled={Boolean(busy)}>
                        {busy === "validating" ? "Validando..." : `Validar${attempts > 0 ? ` (${attempts})` : ""}`}
                      </button>
                    </div>
                    {attempts > 0 ? <p className="bill-mdl-hint">Intento {attempts}. Verifica el número antes de reintentar.</p> : null}
                  </div>
                  <p className="bill-mdl-hint" style={{ color: "#475467" }}>
                    Sin RUC la factura sale como consumidor final a tu nombre.
                  </p>
                </>
              )}
            </section>
          ) : (
            <section className="bill-mdl-card">
              <h3 className="bill-mdl-card-title">Datos del usuario</h3>
              <div className="bill-mdl-summary">
                <div className="bill-mdl-row"><span>Nombre</span><strong>{name}</strong></div>
                <div className="bill-mdl-row"><span>RUC</span><strong>{ruc}</strong></div>
                <div className="bill-mdl-row"><span>DV</span><strong>{dv}</strong></div>
              </div>
              <div className="bill-mdl-actions">
                <button type="button" className="bill-mdl-btn bill-mdl-btn--ghost" onClick={resetRuc}>Ingresar nuevo RUC</button>
                <button type="button" className="bill-mdl-btn bill-mdl-btn--ghost" onClick={() => { setValidated(false); setMessage(null); }}>Cambiar datos</button>
              </div>
            </section>
          )}

          {message ? <p className={`bill-mdl-status bill-mdl-status--${message.kind}`}>{message.text}</p> : null}

          {saved ? (
            <button type="button" className="bill-mdl-remove" onClick={remove} disabled={Boolean(busy)}>
              Quitar personalización (consumidor final)
            </button>
          ) : null}
        </div>

        <div className="bill-mdl-foot">
          <button type="button" className="bill-mdl-btn bill-mdl-btn--ghost" onClick={close} disabled={Boolean(busy)}>Cerrar</button>
          <button type="button" className="bill-mdl-btn bill-mdl-btn--save" onClick={save} disabled={Boolean(busy)}>
            {busy === "saving" ? "Guardando..." : "Guardar datos"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
